import { ExternalLink } from 'lucide-react';
import type { RichBlock, RichInline } from './rich-text.types';

/**
 * Renders the structured copy model as React elements.
 *
 * Every outbound link opens in a new tab with a visible indicator, which the Contact story requires of
 * all of them (BFL-1560 AC-009) and the global Definition of Done requires of every external link.
 * `rel="noreferrer"` goes with `target="_blank"` so the destination cannot reach `window.opener`.
 */
export function RichText({ blocks }: { blocks: readonly RichBlock[] }): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      {blocks.map((block, index) => (
        <Block key={index} block={block} />
      ))}
    </div>
  );
}

function Block({ block }: { block: RichBlock }): React.JSX.Element {
  switch (block.kind) {
    case 'subheading':
      return <h4 className="text-ink text-sm font-semibold">{block.text}</h4>;

    case 'list':
      return (
        <ul className="ml-5 flex list-disc flex-col gap-1.5">
          {block.items.map((item, index) => (
            <li key={index}>
              <Inlines content={item} />
            </li>
          ))}
        </ul>
      );

    case 'address':
      return (
        <address className="text-ink pl-1 text-sm not-italic">
          {block.lines.map((line) => (
            <span key={line} className="block">
              {line}
            </span>
          ))}
        </address>
      );

    case 'paragraph':
      return (
        <p>
          <Inlines content={block.content} />
        </p>
      );
  }
}

function Inlines({ content }: { content: readonly RichInline[] }): React.JSX.Element {
  return (
    <>
      {content.map((run, index) => {
        if (typeof run === 'string') {
          return <span key={index}>{run}</span>;
        }

        if ('bold' in run) {
          return (
            <strong key={index} className="text-ink font-semibold">
              {run.bold}
            </strong>
          );
        }

        const external = run.href.startsWith('http');

        return (
          <a
            key={index}
            href={run.href}
            {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
            className="text-brand-500 inline-flex items-baseline gap-1 underline"
          >
            {run.link}
            {external && (
              <ExternalLink aria-hidden="true" className="h-3 w-3 flex-none self-center" />
            )}
            {external && <span className="sr-only">(opens in a new tab)</span>}
          </a>
        );
      })}
    </>
  );
}
